import React, { useMemo, useState, useCallback, useRef, useEffect } from "react";
import { Box, Typography, Grid2 } from "@mui/material";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import dayjs from "dayjs";
import ApexStackedColumnV2 from "../../../Charts/Column/ApexStackedColumnV2";
import { formatDateRange } from "../../../Helper/Analytics/AnalyticsHelper";
import { formatDateRangeWithSmartYear } from "../../../Helper/DatePicker/DateFormatters";
import StateOfUseDrillDownModal from "./ChartDrillDown/StateOfUseDrillDownModal";
import {
  getStateOfUseTrendLevel1TableConfig,
  getStateOfUseTrendLevel1Data,
  getStateOfUseTrendLevel2TableConfig,
  getStateOfUseTrendLevel2Data,
} from "./ChartDrillDown/ChartDrillDownTable";

// ─── Legend display order (top → bottom as shown in the legend row) ───────────
const LEGEND_ITEMS = [
  { name: "Discharging", color: "#1565C0" },
  { name: "Charging",    color: "#2DB55D" },
  { name: "Offline",     color: "#F48989" },
];

// ─── ApexCharts stacks bottom → top by series array order.
//     Reverse so Offline sits at the bottom and Discharging at the top.
const SERIES_COLORS = ["#F48989", "#2DB55D", "#1565C0"];

// ─── Granularity rules ────────────────────────────────────────────────────────
//   ≤ 14 days  → daily
//   15–60 days → weekly
//   > 60 days  → monthly
const getGranularityForDateRange = (startDate, endDate) => {
  if (!startDate || !endDate) return "weekly";
  const diff = dayjs(endDate).diff(dayjs(startDate), "day")+1;
  if (diff <= 14) return "daily";
  if (diff <= 60) return "weekly";
  return "monthly";
};

// ─── Temp data cycling pattern (values always sum to 100) ────────────────────
const VALUE_PATTERN = [
  { discharging: 55, charging: 35, offline: 10 },
  { discharging: 50, charging: 40, offline: 7  },
  { discharging: 48, charging: 35, offline: 9  },
  { discharging: 52, charging: 38, offline: 8  },
  { discharging: 58, charging: 32, offline: 9  },
  { discharging: 45, charging: 42, offline: 8  },
  { discharging: 60, charging: 30, offline: 8  },
  { discharging: 47, charging: 49, offline: 8  },
];

const generateTempTrendData = (startDate, endDate) => {
  const granularity = getGranularityForDateRange(startDate, endDate);
  const start = dayjs(startDate);
  const end   = dayjs(endDate);
  const entries = [];
  let i = 0;

  if (granularity === "daily") {
    let cur = start;
    while (cur.diff(end, "day") <= 0) {
      entries.push({ period: cur.format("D MMM"), ...VALUE_PATTERN[i % VALUE_PATTERN.length] });
      cur = cur.add(1, "day");
      i++;
    }
  } else if (granularity === "weekly") {
    let cur = start;
    while (cur.diff(end, "day") <= 0) {
      const potentialEnd = cur.add(6, "day");
      const weekEnd = potentialEnd.diff(end, "day") > 0 ? end : potentialEnd;
      entries.push({ period: `${cur.format("D MMM")} - ${weekEnd.format("D MMM")}`, ...VALUE_PATTERN[i % VALUE_PATTERN.length] });
      cur = cur.add(7, "day");
      i++;
    }
  } else {
    // monthly
    const endMonth = end.startOf("month");
    let cur = start.startOf("month");
    while (cur.diff(endMonth, "day") <= 0) {
      entries.push({ period: cur.format("MMM YYYY"), ...VALUE_PATTERN[i % VALUE_PATTERN.length] });
      cur = cur.add(1, "month");
      i++;
    }
  }

  return entries;
};

// ─── Component ────────────────────────────────────────────────────────────────
const StateOfUseTrendChart = ({ startDate, endDate, onDetailsClick }) => {
  // Re-generate data whenever the date range changes
  const tempData = useMemo(() => generateTempTrendData(startDate, endDate), [startDate, endDate]);

  const categories = useMemo(() => tempData.map((d) => d.period), [tempData]);

  // Series order: bottom → top (Offline first, Discharging last)
  const series = useMemo(() => [
    { name: "Offline",     data: tempData.map((d) => d.offline) },
    { name: "Charging",    data: tempData.map((d) => d.charging) },
    { name: "Discharging", data: tempData.map((d) => d.discharging) },
  ], [tempData]);

  // Drill-down modal state
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedDataPoint, setSelectedDataPoint] = useState(null);
  const [selectedIdx, setSelectedIdx] = useState(null);

  const handleModalClose = useCallback(() => {
    setModalOpen(false);
    setSelectedDataPoint(null);
    setSelectedIdx(null);
  }, []);

  // Keep categories accessible in the stable click handler without re-creation
  const categoriesRef = useRef(categories);
  useEffect(() => { categoriesRef.current = categories; }, [categories]);

  const handleDataPointSelection = useCallback((_event, _chartContext, config) => {
    const idx = config?.dataPointIndex;
    if (idx === undefined || idx < 0) return;
    const period = categoriesRef.current[idx];
    setSelectedDataPoint({ period });
    setSelectedIdx(idx);
    setModalOpen(true);
  }, []);

  // Modal header subtitle: "State-of-Use • {dateRange}"
  const buildModalTitle = useCallback(() => {
    const dateRange = formatDateRangeWithSmartYear(startDate, endDate);
    return (
      <Grid2 container gap={1} alignItems="center">
        <Typography variant="fw500s16c8F8F8F" sx={{ lineHeight: "28px" }}>
          State-of-Use
        </Typography>
        <Typography variant="fw500s14c686868" sx={{ lineHeight: "20px" }}>{"•"}</Typography>
        <Typography variant="fw500s14c686868" sx={{ lineHeight: "20px" }}>{dateRange}</Typography>
      </Grid2>
    );
  }, [startDate, endDate]);

  // Level 2 back-navigation title: "{project} / Details"
  const buildLevel2Title = useCallback((level2Record) => {
    const project = level2Record?.project || "Vehicles";
    return `${project} / Details`;
  }, []);

  const level1TableConfig = useMemo(() => getStateOfUseTrendLevel1TableConfig(), []);
  const level2TableConfig = useMemo(() => getStateOfUseTrendLevel2TableConfig(), []);

  return (
    <>
    <Box sx={{ bgcolor: "#fff", borderRadius: "8px", border: "1px solid #E6E6E6", maxWidth: 'calc(100vw - 102px)' }}>

      {/* ── Header ────────────────────────────────────────────────────────── */}
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", px: "28px", pt: "24px" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography variant="fw600s16lh24c444B51" sx={{ color: "#2E2E2E" }}>
            State-of-Use Trend
          </Typography>
          {onDetailsClick && (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                cursor: "pointer",
                "&:hover": {
                  "& .MuiTypography-root": { color: "#4D44D8" },
                  "& .MuiSvgIcon-root": { color: "#4D44D8" },
                },
              }}
              onClick={onDetailsClick}
            >
              <Typography variant="fw500s12lh20c000000" sx={{ color: "#6D72F6" }}>
                Details
              </Typography>
              <ChevronRightIcon sx={{ color: "#6D72F6", fontSize: "18px" }} />
            </Box>
          )}
        </Box>
        <Typography variant="fw400s12c686868" sx={{ lineHeight: "20px" }}>
          {formatDateRange(startDate, endDate)}
        </Typography>
      </Box>

      {/* ── Chart (legend is rendered internally by ApexStackedColumnV2) ─── */}
      <Box sx={{ px: "20px", pb: "8px" }}>
        <ApexStackedColumnV2
          chartId="state-of-use-trend-chart"
          series={series}
          categories={categories}
          colors={SERIES_COLORS}
          height={300}
          columnWidth="45%"
          yAxisTitle="Distribution (%)"
          yMin={0}
          yMax={100}
          yTickAmount={5}
          tooltipYFormatter={(val) => `${val}%`}
          legendItems={LEGEND_ITEMS}
          legendJustify="center"
          onDataPointSelection={handleDataPointSelection}
          selectedIndex={modalOpen ? selectedIdx : null}
        />
      </Box>

    </Box>

    {/* ── Drill-down modal ────────────────────────────────────────────────── */}
    <StateOfUseDrillDownModal
      open={modalOpen}
      onClose={handleModalClose}
      title={buildModalTitle}
      selectedDataPoint={selectedDataPoint}
      tableConfigs={level1TableConfig}
      getTableData={getStateOfUseTrendLevel1Data}
      level2TableConfigs={level2TableConfig}
      level2GetTableData={getStateOfUseTrendLevel2Data}
      level2Title={buildLevel2Title}
    />
    </>
  );
};

export default StateOfUseTrendChart;
